import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import StatusPill from "@/components/StatusPill";
import Timeline from "@/components/Timeline";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, LockKey, ShieldCheck, ArrowClockwise } from "@phosphor-icons/react";

export default function OrderDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [rep, setRep] = useState({ reason: "invalid_email", explanation: "" });
  const [showRep, setShowRep] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setData(data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="p-10">Loading…</div>;
  const { order, delivery, invoice, replacements } = data;
  const canDownload = ["ready", "delivered", "completed"].includes(order.status);

  const download = async (fileId) => {
    const { data } = await api.get(`/files/${fileId}/signed-url`);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download?token=${data.token}`;
    window.open(url, "_blank");
  };

  const submitRep = async () => {
    if (!rep.explanation) { toast.error("Add an explanation"); return; }
    await api.post(`/orders/${id}/replacements`, rep);
    toast.success("Replacement request submitted");
    setShowRep(false); setRep({ reason: "invalid_email", explanation: "" });
    load();
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs text-muted-foreground">{order.reference}</span>
              <StatusPill status={order.status} size="lg" />
            </div>
            <h1 className="font-heading font-black tracking-tighter text-3xl md:text-4xl">Order · {order.option_key === "verified" ? "Verified" : "Standard"} Data</h1>
            <div className="mt-3 text-sm text-muted-foreground">
              <span>Amount: <b className="text-foreground">{order.currency} {order.amount?.toLocaleString()}</b></span>
              {order.expected_delivery_date && <span className="ml-6">ETA: <b className="text-foreground">{order.expected_delivery_date}</b></span>}
              {order.final_record_count && <span className="ml-6">Delivered records: <b className="text-foreground">{order.final_record_count.toLocaleString()}</b></span>}
            </div>
          </div>

          {/* Delivery panel */}
          <div className={`border p-6 ${canDownload ? "border-emerald-300 bg-emerald-50" : "border-border bg-white"}`}>
            <div className="flex items-center gap-3 mb-3">
              {canDownload ? <ShieldCheck size={22} weight="duotone" className="text-emerald-700" /> : <LockKey size={22} weight="duotone" className="text-muted-foreground" />}
              <div>
                <div className="font-semibold">{canDownload ? "Your file is ready" : "Awaiting QA approval"}</div>
                <div className="text-xs text-muted-foreground">Encrypted storage · time-limited signed links · every download is logged.</div>
              </div>
            </div>
            {canDownload && delivery && (
              <div className="flex flex-wrap gap-2 mt-4">
                <Button data-testid={TID.detail.downloadFile} onClick={() => download(delivery.file_id)} className="rounded-sm">
                  <Download size={14} className="mr-2" /> Download {delivery.original_filename}
                </Button>
                {invoice && (
                  <Button variant="outline" onClick={() => download(invoice.file_id)} className="rounded-sm">
                    <Download size={14} className="mr-2" /> Invoice
                  </Button>
                )}
                {data.quality && (
                  <Button variant="outline" onClick={() => alert(JSON.stringify(data.quality, null, 2))} className="rounded-sm">
                    View quality summary
                  </Button>
                )}
              </div>
            )}
            {!canDownload && (
              <p className="text-xs text-muted-foreground mt-2">The final file becomes available after quality review approves the delivery. You'll be notified.</p>
            )}
          </div>

          {/* Replacement */}
          {canDownload && (
            <div className="border border-border bg-white p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-semibold">Replacement requests</div>
                  <div className="text-xs text-muted-foreground">Within 30 days of delivery.</div>
                </div>
                <Button variant="outline" onClick={() => setShowRep(!showRep)} className="rounded-sm" data-testid="order-replacement-toggle">
                  <ArrowClockwise size={14} className="mr-2" /> {showRep ? "Cancel" : "New replacement"}
                </Button>
              </div>
              {showRep && (
                <div className="space-y-3 mt-4">
                  <div>
                    <Label>Reason</Label>
                    <Select value={rep.reason} onValueChange={(v) => setRep({ ...rep, reason: v })}>
                      <SelectTrigger className="mt-1" data-testid="order-rep-reason"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="invalid_email">Invalid email</SelectItem>
                        <SelectItem value="wrong_company">Incorrect current company</SelectItem>
                        <SelectItem value="wrong_title">Incorrect job title</SelectItem>
                        <SelectItem value="duplicate">Duplicate record</SelectItem>
                        <SelectItem value="targeting_mismatch">Targeting mismatch</SelectItem>
                        <SelectItem value="missing_field">Missing agreed field</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Explanation</Label>
                    <Textarea data-testid="order-rep-explanation" rows={3} value={rep.explanation} onChange={(e) => setRep({ ...rep, explanation: e.target.value })} className="mt-1" />
                  </div>
                  <Button data-testid="order-rep-submit" onClick={submitRep} className="rounded-sm">Submit replacement request</Button>
                </div>
              )}
              {replacements?.length > 0 && (
                <div className="mt-5 space-y-2">
                  {replacements.map((r) => (
                    <div key={r.case_id} className="border border-border p-3 text-sm flex items-center justify-between">
                      <div>
                        <div className="font-mono text-[11px] text-muted-foreground">{r.case_id}</div>
                        <div>{r.reason.replace(/_/g, " ")} — {r.explanation}</div>
                      </div>
                      <StatusPill status={"replacement_" + (r.status === "submitted" ? "requested" : r.status === "in_progress" ? "in_progress" : r.status === "delivered" ? "delivered" : "requested")} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="border border-border bg-white p-5">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-4">Progress</div>
            <Timeline currentStatus={order.status} />
          </div>
          <Link to={`/requests/${order.request_id}`} className="text-sm text-primary hover:underline">View original request →</Link>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
